import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import PreviewTemplate from 'components/common/previewTemplate';
import CreateTemplateForm from 'pages/Client/CreateTemplateForm';
import { useGetBusinessUnitTemplatesMutation } from 'services/api/businessUnits.api';

interface BusinessUnitTemplatesProps {
  businessUnitId: number;
}

const BusinessUnitTemplates: React.FC<BusinessUnitTemplatesProps> = ({ businessUnitId }) => {
  const [getTemplates, { data: templates, isLoading, error }] = useGetBusinessUnitTemplatesMutation();
  const [openNewTemplate, setOpenNewTemplate] = useState(false);

  useEffect(() => {
    getTemplates(businessUnitId).catch(console.error); // eslint-disable-line no-console
  }, [businessUnitId, getTemplates]);

  if (isLoading) {
    return <Typography>Cargando templates...</Typography>;
  }

  if (error) {
    return <Typography color="error">Error al cargar los templates</Typography>;
  }

  if (templates === undefined) {
    return null;
  }

  return (
    <Box>
      <Button variant="contained" onClick={() => setOpenNewTemplate(true)} sx={{ mb: 2 }}>
        Crear nuevo template
      </Button>
      <Grid container spacing={3}>
        {templates.map((template) => (
          <Grid item xs={12} md={4} key={template.name}>
            <Box mb={2}>
              <Box>
                <PreviewTemplate
                  cardTitle={`${template.name} (${template.category})`}
                  headerEnabled={template.components.some((c) => c.type === 'HEADER')}
                  headerText={template.components.find((c) => c.type === 'HEADER')?.text || null}
                  headerFormat={template.components.find((c) => c.type === 'HEADER')?.format || null}
                  headerFileUrl={
                    template.components.find((c) => c.type === 'HEADER' && c.format === 'IMAGE')?.example
                      ?.header_handle?.[0] || null
                  }
                  bodyText={template.components.find((c) => c.type === 'BODY')?.text || ''}
                  bodyVariableExamples={
                    template.components.find((c) => c.type === 'BODY')?.example?.body_text?.[0] || null
                  }
                  footerEnabled={template.components.some((c) => c.type === 'FOOTER')}
                  footerText={template.components.find((c) => c.type === 'FOOTER')?.text || null}
                  buttons={template.components.find((c) => c.type === 'BUTTONS')?.buttons || null}
                  description={
                    <Box m={1}>
                      <Typography>Variables:</Typography>
                      {template.components
                        .find((c) => c.type === 'BODY')
                        ?.example?.body_text?.[0].map((text) => {
                          return <li key={text}>{text}</li>;
                        })}
                    </Box>
                  }
                />
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      <Modal open={openNewTemplate} onClose={() => setOpenNewTemplate(false)}>
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', md: '70%' },
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: (theme) => theme.shadows[24],
            p: 4,
          }}
        >
          <CreateTemplateForm businessUnitId={businessUnitId} />
        </Paper>
      </Modal>
    </Box>
  );
};

export default BusinessUnitTemplates;
